<template>
  <div class="ror-preview">
    <header class="ror-preview__head">
      <h2 class="ror-preview__name">{{ regiment.name || '(unnamed regiment)' }}</h2>
      <div class="ror-preview__meta">
        <span>{{ regiment.points }} pts</span>
        <span v-if="regiment.legends">· Legends</span>
        <span v-if="regiment.sourceArmies.length">· {{ regiment.sourceArmies.join(', ') }}</span>
      </div>
    </header>

    <section v-if="regiment.upgrades[0]" class="ror-preview__section">
      <div class="st-eyebrow">Abilities</div>
      <GroupPreview :group="regiment.upgrades[0]" hide-name />
    </section>

    <section v-if="regiment.units.length" class="ror-preview__section">
      <div class="st-eyebrow">Warscrolls</div>
      <ul class="ror-preview__units">
        <li v-for="entry in regiment.units" :key="entry.unit.name">
          <span v-if="entry.max > 0 && entry.max !== entry.count">
            {{ entry.count }}-{{ entry.max }} x {{ entry.unit.name }}
          </span>
          <span v-else>{{ entry.count }} x {{ entry.unit.name }}</span>
          <span v-if="entry.points"> · {{ entry.points }} pts</span>
        </li>
      </ul>
      <WarscrollPreview
        v-for="entry in regiment.units"
        :key="entry.unit.id"
        :unit="entry.unit"
        :army-keyword="regiment.sourceArmies[0]"
      />
    </section>

    <section v-if="regiment.selectableIn.length" class="ror-preview__section">
      <div class="st-eyebrow">Allowed Armies</div>
      <ul class="ror-preview__allowed">
        <li v-for="army in regiment.selectableIn" :key="army">{{ army }}</li>
      </ul>
    </section>

    <p v-if="isEmpty" class="ror-preview__empty">
      Nothing authored yet - add units, allowed armies, or regiment abilities.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IRegimentOfRenown } from '@scrolldex/shared';
import GroupPreview from './GroupPreview.vue';
import WarscrollPreview from './WarscrollPreview.vue';

const props = defineProps<{ regiment: IRegimentOfRenown }>();

const isEmpty = computed(
  () =>
    !props.regiment.units.length &&
    !props.regiment.selectableIn.length &&
    !props.regiment.sourceArmies.length &&
    !(props.regiment.upgrades[0]?.abilities.length || props.regiment.upgrades[0]?.rules.length),
);
</script>

<style scoped>
.ror-preview {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.ror-preview__head,
.ror-preview__section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.ror-preview__name {
  font-size: var(--st-text-xl);
  font-weight: var(--st-weight-bold);
}
.ror-preview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
.ror-preview__units,
.ror-preview__allowed {
  margin: 0;
  padding-left: var(--st-space-4);
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
.ror-preview__empty {
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
</style>
